import { useState } from "react";
import { Clock, DollarSign, Play, Square, RotateCcw, CheckCircle2, Loader2 } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";

interface RpmBillingTrackerProps {
  memberId: string;
  patientName: string;
  elapsedSeconds: number;
  isActive: boolean;
  isEligible: boolean;
  onStart: () => void;
  onStop: () => void;
  onReset: () => void;
  onClaimGenerated: () => void;
}

function formatTime(totalSeconds: number): string {
  const mins = Math.floor(totalSeconds / 60);
  const secs = totalSeconds % 60;
  return `${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
}

const RPM_CPT_CODES = [
  { code: "99457", label: "RPM Interactive Communication (20+ min)", amount: 100, description: "Remote physiologic monitoring treatment management services, first 20 minutes" },
  { code: "99091", label: "RPM Data Review & Interpretation", amount: 300, description: "Collection and interpretation of physiologic data, 30+ minutes" },
];

export function RpmBillingTracker({
  memberId,
  patientName,
  elapsedSeconds,
  isActive,
  isEligible,
  onStart,
  onStop,
  onReset,
  onClaimGenerated,
}: RpmBillingTrackerProps) {
  const [showClaimDialog, setShowClaimDialog] = useState(false);
  const [selectedCodes, setSelectedCodes] = useState<string[]>(["99457", "99091"]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { user } = useAuth();
  const { toast } = useToast();

  const progressPercent = Math.min((elapsedSeconds / (20 * 60)) * 100, 100);
  const minutesReviewed = Math.floor(elapsedSeconds / 60);

  const toggleCode = (code: string) => {
    setSelectedCodes((prev) =>
      prev.includes(code) ? prev.filter((c) => c !== code) : [...prev, code]
    );
  };

  const handleGenerateClaims = async () => {
    if (!user?.id || selectedCodes.length === 0) return;

    setIsSubmitting(true);
    try {
      // Look up provider record
      const { data: provider, error: providerError } = await supabase
        .from("providers")
        .select("id")
        .eq("user_id", user.id)
        .maybeSingle();

      if (providerError) throw providerError;

      const providerId = provider?.id || user.id;
      const today = new Date();
      const scheduledDate = today.toISOString().split("T")[0];
      const scheduledTime = today.toTimeString().slice(0, 8);

      // Create a telehealth_session for each selected CPT code
      for (const code of selectedCodes) {
        const cptInfo = RPM_CPT_CODES.find((c) => c.code === code);
        const { error } = await supabase.from("telehealth_sessions").insert({
          provider_id: providerId,
          member_id: memberId,
          scheduled_date: scheduledDate,
          scheduled_time: scheduledTime,
          visit_type: "rpm",
          visit_reason: cptInfo?.description || "Remote Patient Monitoring",
          status: "completed",
          cpt_code: code,
          actual_duration_minutes: minutesReviewed,
          billing_status: "pending",
          duration_minutes: minutesReviewed,
          started_at: new Date(Date.now() - elapsedSeconds * 1000).toISOString(),
          ended_at: new Date().toISOString(),
        });

        if (error) throw error;
      }

      toast({
        title: "RPM billing claims generated",
        description: `${selectedCodes.length} claim(s) created for ${patientName} (${minutesReviewed} min review)`,
      });

      setShowClaimDialog(false);
      onClaimGenerated();
      onReset();
    } catch (error: any) {
      toast({
        title: "Error generating claims",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Card className={cn(
        "border transition-all",
        isEligible && "border-success shadow-sm shadow-success/10",
        isActive && !isEligible && "border-primary/50"
      )}>
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2 text-sm font-medium">
              <Clock className="h-4 w-4 text-muted-foreground" />
              RPM Review Timer
            </CardTitle>
            {isEligible && (
              <Badge variant="default" className="bg-success text-success-foreground gap-1">
                <CheckCircle2 className="h-3 w-3" />
                Billable
              </Badge>
            )}
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          {/* Timer Display */}
          <div className="text-center">
            <p className={cn(
              "text-3xl font-mono font-bold tabular-nums",
              isActive ? "text-primary" : "text-muted-foreground"
            )}>
              {formatTime(elapsedSeconds)}
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              {isEligible
                ? "20-minute threshold reached"
                : `${Math.max(0, 20 - minutesReviewed)} min until billable`}
            </p>
          </div>

          {/* Progress Bar */}
          <div className="space-y-1">
            <Progress
              value={progressPercent}
              className={cn("h-2", isEligible && "[&>div]:bg-success")}
            />
            <div className="flex justify-between text-xs text-muted-foreground">
              <span>0 min</span>
              <span>20 min</span>
            </div>
          </div>

          {/* Controls */}
          <div className="flex gap-2">
            {!isActive ? (
              <Button size="sm" className="flex-1 gap-1" onClick={onStart}>
                <Play className="h-3 w-3" />
                {elapsedSeconds > 0 ? "Resume" : "Start"}
              </Button>
            ) : (
              <Button size="sm" variant="outline" className="flex-1 gap-1" onClick={onStop}>
                <Square className="h-3 w-3" />
                Pause
              </Button>
            )}
            {elapsedSeconds > 0 && (
              <Button size="sm" variant="ghost" onClick={onReset}>
                <RotateCcw className="h-3 w-3" />
              </Button>
            )}
          </div>

          {/* Generate Claim Button */}
          {isEligible && (
            <Button
              className="w-full gap-2 bg-success hover:bg-success/90 text-success-foreground"
              onClick={() => setShowClaimDialog(true)}
            >
              <DollarSign className="h-4 w-4" />
              Generate RPM Billing Claim
            </Button>
          )}
        </CardContent>
      </Card>

      {/* Claim Generation Dialog */}
      <Dialog open={showClaimDialog} onOpenChange={setShowClaimDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Generate RPM Billing Claims</DialogTitle>
            <DialogDescription>
              Create billing claims for {patientName} based on {minutesReviewed} minutes of RPM data review.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="rounded-lg bg-muted/50 p-3 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Patient</span>
                <span className="font-medium">{patientName}</span>
              </div>
              <div className="flex justify-between mt-1">
                <span className="text-muted-foreground">Review Duration</span>
                <span className="font-medium">{minutesReviewed} minutes</span>
              </div>
              <div className="flex justify-between mt-1">
                <span className="text-muted-foreground">Date</span>
                <span className="font-medium">{new Date().toLocaleDateString()}</span>
              </div>
            </div>

            <div className="space-y-3">
              <Label className="text-sm font-medium">Select CPT Codes</Label>
              {RPM_CPT_CODES.map((cpt) => (
                <div
                  key={cpt.code}
                  className={cn(
                    "flex items-start gap-3 rounded-lg border p-3 cursor-pointer transition-colors",
                    selectedCodes.includes(cpt.code)
                      ? "border-primary bg-primary/5"
                      : "border-border hover:bg-muted/50"
                  )}
                  onClick={() => toggleCode(cpt.code)}
                >
                  <Checkbox
                    checked={selectedCodes.includes(cpt.code)}
                    onCheckedChange={() => toggleCode(cpt.code)}
                  />
                  <div className="flex-1">
                    <div className="flex items-center justify-between">
                      <span className="font-medium text-sm">CPT {cpt.code}</span>
                      <span className="text-sm font-semibold">${cpt.amount}</span>
                    </div>
                    <p className="text-xs text-muted-foreground mt-0.5">{cpt.label}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="flex justify-between items-center rounded-lg bg-muted p-3 font-medium">
              <span>Total</span>
              <span className="text-lg">
                ${RPM_CPT_CODES.filter((c) => selectedCodes.includes(c.code)).reduce((sum, c) => sum + c.amount, 0)}
              </span>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowClaimDialog(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleGenerateClaims}
              disabled={isSubmitting || selectedCodes.length === 0}
              className="gap-2"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Generating...
                </>
              ) : (
                <>
                  <DollarSign className="h-4 w-4" />
                  Generate {selectedCodes.length} Claim{selectedCodes.length !== 1 ? "s" : ""}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
